import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { TextField, MenuItem, InputAdornment, Tooltip, Button, Typography, Box, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import { getRepositoryDetails } from '../../apis/repository';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../StatusBadge';
import CreateBatchDialog from './CreateBatchDialog';
import { VisibilityOutlined, Delete, Add, ArrowBack } from '@mui/icons-material';
import { useDebounce } from '../../hooks/useDebounce';
import { useSelector } from 'react-redux';
import { includes } from 'lodash';
import { formatDate } from '../../lib/utils';

const RepositoryBatchList = ({ repositoryId, pageSize = 10 }) => {
    const navigate = useNavigate();
    const permissions = useSelector((state) => state.auth.permissions);

    const [batches, setBatches] = useState([]);
    const [repository, setRepository] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [totalCount, setTotalCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({
        pageSize,
        page: 0,
    });
    const [createBatchDialogOpen, setCreateBatchDialogOpen] = useState(false);

    const debouncedSearch = useDebounce(search, 500);

    useEffect(() => {
        if (repositoryId) loadBatches();
    }, [repositoryId, paginationModel.page, paginationModel.pageSize, debouncedSearch, statusFilter]);

    const loadBatches = async () => {
        if (!repositoryId || !isPermitted('list')) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await getRepositoryDetails(
                repositoryId,
                paginationModel.page + 1,
                paginationModel.pageSize,
                search,
                statusFilter
            );

            // Extract batches from the response
            const batchResults = response.batches || response.results || [];
            setBatches(batchResults);
            setTotalCount(batchResults.length);
            // Set repository details for title
            setRepository(response.repository || response);
        } catch (error) {
            // console.error('Error loading repository batches:', error);
            toast.error('Failed to load repository batches');
            setBatches([]);
            setTotalCount(0);
            setRepository(null);
        } finally {
            setLoading(false);
        }
    };

    const handleViewBatch = (batchId) => {
        navigate('/repository-management/' + repositoryId + '/batches/' + batchId + '/files');
    };

    const handleDeleteBatch = async (batchId) => {
        try {
            // Implement batch deletion
            await new Promise((resolve) => setTimeout(resolve, 500));
            setBatches((prev) => prev.filter((batch) => batch.id !== batchId));
            setTotalCount((prev) => prev - 1);
            toast.success('Batch deleted successfully');
        } catch (error) {
            // console.error('Error deleting batch:', error);
            toast.error('Failed to delete batch');
        }
    };

    const handleCreateBatch = () => {
        setCreateBatchDialogOpen(true);
    };

    const handleCreateBatchSuccess = () => {
        toast.success('Batch created successfully');
        loadBatches(); // Refresh the list
    };

    const isPermitted = (value) => includes(permissions?.batches, value);

    const columns = [
        {
            field: 'batch_name',
            headerName: 'Batch Name',
            flex: 2,
            renderCell: (params) => params?.value || '-',
        },
        {
            field: 'path',
            headerName: 'Path',
            flex: 1.5,
            renderCell: (params) => params?.value || '-',
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 2,
            renderCell: (params) => {
                const status = params?.value || '-';
                return <StatusBadge status={status} />;
            },
        },
        {
            field: 'remarks',
            headerName: 'Remarks',
            flex: 2,
            renderCell: (params) => params?.value || '-',
        },
        {
            field: 'created_at',
            headerName: 'Created',
            flex: 1.5,
            valueFormatter: formatDate,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1.5,
            sortable: false,
            renderCell: (params) => {
                const batch = params?.row;
                if (!batch) return null;

                return (
                    <Box>
                        {/* View */}
                        {isPermitted('view') ? (
                            <Tooltip title="View Details">
                                <IconButton size="small" onClick={() => handleViewBatch(batch.id)} color="primary">
                                    <VisibilityOutlined />
                                </IconButton>
                            </Tooltip>
                        ) : null}

                        {/* Delete */}
                        {isPermitted('delete') ? (
                            <Tooltip title="Delete Batch">
                                <IconButton size="small" onClick={() => handleDeleteBatch(batch.id)} color="error">
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    </Box>
                );
            },
        },
    ];

    return (
        // Add the back button here outside the box
        <Box>
            <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/repository-management')}
                sx={{ minWidth: 'auto', my: 1, border: 0 }}
            >
                Back
            </Button>

            <Box variant="div" className="custom_table_container">
                <Box variant="div" className="table_filters">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography variant="h6" fontWeight={600}>
                            {repositoryId
                                ? `Repository - ${repository?.name || `Repository ${repositoryId}`}`
                                : 'All Batches'}
                        </Typography>
                    </Box>
                    <Box className="filter_box">
                        <TextField
                            size="small"
                            placeholder="Search batches..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <span className="material-symbols-rounded">search</span>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <TextField
                            select
                            label="Filter by status"
                            size="small"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ minWidth: 180 }}
                        >
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="queued">Queued</MenuItem>
                            <MenuItem value="processing">Processing</MenuItem>
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </TextField>
                        {isPermitted('add') ? (
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={handleCreateBatch}
                                disabled={repository?.status !== 'approved'}
                                title={
                                    repository?.status !== 'approved'
                                        ? 'Repository must be approved to create batches'
                                        : 'Create new batch'
                                }
                            >
                                Create Batch
                            </Button>
                        ) : null}
                    </Box>
                </Box>

                <Box variant="div" className="custom_table_responsive">
                    <DataGrid
                        rows={batches}
                        columns={columns}
                        getRowId={(row) => row.id}
                        autoHeight
                        pageSizeOptions={[5, 10, 25, 50]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pagination
                        paginationMode="client"
                        loading={loading}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        localeText={{
                            noRowsLabel: 'No Batches Found',
                            sortLabel: '',
                        }}
                        rowCount={totalCount}
                    />
                </Box>
            </Box>

            <CreateBatchDialog
                open={createBatchDialogOpen}
                onClose={() => setCreateBatchDialogOpen(false)}
                onSuccess={handleCreateBatchSuccess}
                repository={repository}
            />
        </Box>
    );
};

export default RepositoryBatchList;
